import React, { useEffect, useState } from "react";
import { Customer, getCustomer } from "@/api/customers";
import {
  getUsersByCustomerId,
  CustomerUser,
  TmsCustomerUser,
  getTmsUsersByCustomerId,
  CustomerUsersMigrationModel,
  addTmsUsersToCustomer,
  CustomerUserModel,
} from "@/api/users";
import SearchBox from "@/ui-controls/SearchBox";
import CustomerUserDetailList from "@/components/customers/customer_users/CustomerUserDetailList";
import {
  DefaultButton,
  IconButton,
  IContextualMenuProps,
  MessageBar,
  MessageBarType,
  Shimmer,
  ShimmerElementType,
} from "@fluentui/react";
import MigrateTmsCustomerUsersPanel from "@/components/customers/customer_users/MigrateTmsCustomerUsersPanel";
import NewCustomerUserPanel from "@/components/customers/customer_users/NewCustomerUserPanel";
import { useNavigate } from "react-router-dom";

interface Props {
  CustomerId: number;
  onCustomerLoaded?: (customerName: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
}

const CustomerUsers = (props: Props) => {
  const navigate = useNavigate();
  const [loadingCustomerUsers, setLoadingCustomerUsers] =
    useState<boolean>(false);
  const [loadingTmsCustomerUsers, setLoadingTmsCustomerUsers] =
    useState<boolean>(false);
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [customerUsers, setCustomerUsers] = useState<CustomerUser[]>([]);
  const [tmsCustomerUsers, setTmsCustomerUsers] = useState<TmsCustomerUser[]>(
    []
  );
  const [filteredUsers, setFilteredUsers] = useState<CustomerUser[]>([]);
  const [
    migrateCustomerUsersPanelOpenned,
    setMigrateCustomerUsersPanelOpenned,
  ] = useState<boolean>(false);
  const [newUserPanelOpenned, setNewUserPanelOpenned] =
    useState<boolean>(false);
  const [editUserPanelOpened, setEditUserPanelOpened] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<CustomerUser | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);

  useEffect(() => {
    if (props.CustomerId) {
      getCustomerById();
      getCustomerUsers();
      getTmsCustomerUsers();
    }
  }, [props.CustomerId]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-clear warning messages after 8 seconds
  useEffect(() => {
    if (warningMessages.length > 0) {
      const timer = setTimeout(() => {
        setWarningMessages([]);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [warningMessages]);

  const getCustomerUsers = () => {
    setLoadingCustomerUsers(true);
    getUsersByCustomerId(props.CustomerId)
      .then((result) => {
        setCustomerUsers(result);
        setFilteredUsers(result);
        setLoadingCustomerUsers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingCustomerUsers(false);
      });
  };

  const getTmsCustomerUsers = () => {
    setLoadingTmsCustomerUsers(true);
    getTmsUsersByCustomerId(props.CustomerId)
      .then((result) => {
        setTmsCustomerUsers(result);
        setLoadingTmsCustomerUsers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingTmsCustomerUsers(false);
      });
  };

  const getCustomerById = () => {
    getCustomer(props.CustomerId)
      .then((result) => {
        setCustomer(result);
        // Notify parent component about the customer name
        if (props.onCustomerLoaded && result?.Name) {
          props.onCustomerLoaded(result.Name);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    // Error handling would go here in real app
  };

  const handleUserSearch = (searchValue: string) => {
    const filteredUsers = customerUsers.filter(
      (user) =>
        user.EmailAddress.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.TmsUsername?.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredUsers(filteredUsers);
  };

  const startImportUsers = () => {
    setMigrateCustomerUsersPanelOpenned(true);
  };

  const openNewUserPanel = () => {
    setNewUserPanelOpenned(true);
  };

  const openEditUserPanel = (user: CustomerUser) => {
    setSelectedUser(user);
    setEditUserPanelOpened(true);
  };

  const handleShowSuccess = (message: string) => {
    setSuccessMessage(message);
    setWarningMessages([]); // Clear any existing warnings
  };

  const handleShowWarnings = (messages: string[]) => {
    setWarningMessages(messages);
  };

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: "importUsers",
        text: "Import users from customer database",
        onClick: () => {
          startImportUsers();
        },
        iconProps: { iconName: "Import" },
        disabled: loadingTmsCustomerUsers || loadingCustomerUsers,
      },
      {
        key: "addUsers",
        text: "Add users manually",
        iconProps: { iconName: "Add" },
        onClick: () => {
          openNewUserPanel();
        },
      },
    ],
  };

  const continueSave = async (
    tmsCustomerUsers: TmsCustomerUser[],
    useTmsPassword: boolean
  ) => {
    const model = {
      CustomerId: props.CustomerId,
      TmsCustomerUsers: tmsCustomerUsers,
      UseTmsPassword: useTmsPassword,
    } as CustomerUsersMigrationModel;

    try {
      await addTmsUsersToCustomer(props.CustomerId, model);
      getCustomerUsers();
    } catch (error) {
      handleError(error);
      throw error; // Re-throw to let the form handle the error display
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <IconButton
            iconProps={{ iconName: "Back" }}
            title="Back to Customers"
            onClick={() => navigate("/customers")}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
            }}
          />
          <div>
            {props.breadcrumbTitle && (
              <h2 className="page-breadcrumb-title">
                {props.breadcrumbTitle}
              </h2>
            )}
            {props.breadcrumbSubtitle && (
              <div style={{ color: "#605e5c", fontSize: "14px" }}>
                {props.breadcrumbSubtitle}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DefaultButton
            text="Add Users"
            primary
            split
            splitButtonAriaLabel="See 2 options"
            aria-roledescription="split button"
            menuProps={menuProps}
          />
          <SearchBox placeholder="Search" onSearch={handleUserSearch} />
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={() => {
              getCustomerUsers();
              getTmsCustomerUsers();
            }}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
            }}
          />
        </div>
      </div>

      <div>
        <CustomerUserDetailList
          UserList={filteredUsers}
          CustomerId={props.CustomerId}
          onRemoveUsers={() => {
            getCustomerUsers();
          }}
          onEditUser={(user) => {
            openEditUserPanel(user);
          }}
        />
      </div>
      <MigrateTmsCustomerUsersPanel
        customer={customer}
        tmsCustomerUsers={tmsCustomerUsers.filter((u) =>
          u.TmsUsername
            ? !customerUsers.some((cu) => cu.TmsUsername === u.TmsUsername)
            : !customerUsers.some(
                (cu) =>
                  cu.EmailAddress?.toLowerCase() ===
                  u.EmailAddress?.toLowerCase()
              )
        )}
        isPanelOpenned={migrateCustomerUsersPanelOpenned}
        onPanelClosed={() => {
          setMigrateCustomerUsersPanelOpenned(false);
        }}
        onSave={async (
          tmsCustomerUsers: TmsCustomerUser[],
          useTmsPassword: boolean
        ) => {
          return await continueSave(tmsCustomerUsers, useTmsPassword);
        }}
      />

      <NewCustomerUserPanel
        customer={customer}
        isPanelOpenned={newUserPanelOpenned}
        onPanelClosed={() => {
          setNewUserPanelOpenned(false);
        }}
        onSave={(customerUserModel: CustomerUserModel) => {
          getCustomerUsers();
        }}
        onShowSuccess={handleShowSuccess}
        onShowWarnings={handleShowWarnings}
      />
      <NewCustomerUserPanel
        customer={customer}
        isPanelOpenned={editUserPanelOpened}
        isEditMode={true}
        selectedUser={selectedUser}
        onPanelClosed={() => {
          setEditUserPanelOpened(false);
          setSelectedUser(null);
        }}
        onUpdated={() => {
          getCustomerUsers();
        }}
        onShowSuccess={handleShowSuccess}
        onShowWarnings={handleShowWarnings}
      />

      {/* Success message bar */}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {/* Warning message bar */}
      {warningMessages.length > 0 && (
        <MessageBar
          messageBarType={MessageBarType.warning}
          isMultiline={true}
          onDismiss={() => setWarningMessages([])}
          styles={{
            root: {
              position: "fixed",
              bottom: successMessage ? 56 : 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          <div>
            {warningMessages.map((message, index) => (
              <div
                key={index}
                style={{
                  marginBottom:
                    index < warningMessages.length - 1 ? "4px" : "0",
                }}
              >
                {message}
              </div>
            ))}
          </div>
        </MessageBar>
      )}
    </>
  );
};

export default CustomerUsers;
